import { useSelector } from "react-redux"
import { selectPracticeDataStatus } from "../../domain/Practice/loaders/loader"
import { dataStatusesSending } from "../shared/utils"
import { selectProviderDataStatus } from "../../domain/Provider/loader"
import { selectUserDataStatus } from "../../domain/Account/userSlice"
import { selectAccountGuid } from "../authentication/authenticationSlice"
import { selectValidatedHealthAdvocateUser } from "../shared/selectors"
import { Practice, PracticeCompleteness } from "../../domain/Practice/schema"
import { StepStatusEnum } from "../../domain/ViewModel/schema"
import {
  isLocationAddressComplete,
  isLocationContactComplete,
  isLocationDetailsComplete,
  isPracticeAddressComplete,
  isPracticeContactComplete,
} from "../../domain/ViewModel/helpers/practiceHelpers"
import { appRoutes, getRouteWithQueryParams } from "../../routes"
import { Provider, ProviderOnboardingData } from "../../domain/Provider/schema"
import {
  clientFocusDemographicsComplete,
  clientFocusSessionsComplete,
  clientFocusSpecialtiesComplete,
  getAvailabilityStepStatus,
  getDisclosureStepStatus,
  getInsuranceStepStatus,
  getLiabilityInsuranceStepStatus,
  getLocationsStepStatus,
  getQualificationsStepStatus,
  getServiceStepStatus,
  personalInfoContactComplete,
  personInfoDetailsComplete,
} from "../../domain/ViewModel/helpers/providerHelpers"
import { UserDomainState } from "./useUserDomainState"

// Centralized deep link localStorage keys
export const MSAL_DEEP_LINK_URL = "MSAL_DEEP_LINK_URL"
// The fixed query param key to look for - this MUST be used in any backend service that wants to hit this hook
export const DEEP_LINK_QUERY_KEY = "deepLinkTarget"

// TODO: remove this hook
/**
 * Custom hook for determining if a practice is still loading for a given user
 * @return {boolean}
 */
export const usePracticeLoadingForUser = () => {
  const isHaUser = useSelector(selectValidatedHealthAdvocateUser)
  const practiceDataStatus = useSelector(selectPracticeDataStatus)

  // non-ha user can get practice either by:
  // fresh load before auth with a deeplink -> calls getFullPractice,
  // posting when allPracticeNames.length === 0 -> calls postOrSelectPractice,
  // by fetching when allPracticeNames.length === 1 -> calls getFullPractice,
  // otherwise, they need to select the practice manually
  const nonHaUserPracticeLoading = dataStatusesSending([
    practiceDataStatus.fullPracticeDataStatus,
  ])

  const practiceLoadingForUser = isHaUser
    ? dataStatusesSending([practiceDataStatus.fullPracticeDataStatus])
    : nonHaUserPracticeLoading

  return practiceLoadingForUser
}

/**
 * Custom hook for determining if a provider is currently loading for the given user.
 * @return {boolean}
 */
export const useProviderLoadingForUser = () => {
  const isHaUser = useSelector(selectValidatedHealthAdvocateUser)
  const practiceDataStatus = useSelector(selectPracticeDataStatus)
  const providerDataStatus = useSelector(selectProviderDataStatus)

  const domainDataStatuses = isHaUser
    ? [
        practiceDataStatus.fullPracticeDataStatus,
        providerDataStatus.baseProviderDataStatus,
      ]
    : [
        practiceDataStatus.postOrSelectPracticeDataStatus,
        providerDataStatus.baseProviderDataStatus,
      ]

  const providerLoadingForUser = dataStatusesSending(domainDataStatuses)

  return providerLoadingForUser
}

/**
 * Custom hook for determining if the user if still loading
 * @return {boolean}
 */
export const useUserLoading = () => {
  const userId = useSelector(selectAccountGuid)
  const userDataStatus = useSelector(selectUserDataStatus)
  const isHaUser = useSelector(selectValidatedHealthAdvocateUser)

  const userLoading = isHaUser
    ? !userId
    : (userDataStatus.practiceRolesDataStatus === "loading" && !!userId) ||
      userDataStatus.practiceRolesDataStatus === "sending"

  return userLoading
}

/**
 * Custom hook to determine if the fetch domain errors and user domain state errors are in sync
 * based on the practice and provider data statuses and the user domain state.
 *
 * @param {UserDomainState} userDomainState - The current user domain state.
 * @returns {boolean} - Returns `true` if the domain errors are in sync, `false` otherwise.
 */
export const useDomainErrorsInSync = (userDomainState: UserDomainState) => {
  const practiceDataStatus = useSelector(selectPracticeDataStatus)
  const providerDataStatus = useSelector(selectProviderDataStatus)

  const practiceErrorInSync =
    (practiceDataStatus.fullPracticeDataStatus === "error" &&
      !!userDomainState.fetchingError?.practiceId) ||
    (practiceDataStatus.fullPracticeDataStatus !== "error" &&
      !userDomainState.fetchingError?.practiceId)

  const providerErrorInSync =
    (providerDataStatus.baseProviderDataStatus === "error" &&
      !!userDomainState.fetchingError?.providerId) ||
    (providerDataStatus.baseProviderDataStatus !== "error" &&
      !userDomainState.fetchingError?.providerId)

  const domainErrorsInSync = practiceErrorInSync && providerErrorInSync

  return domainErrorsInSync
}

/**
 * Determines the next incomplete practice profile step route based on the practice status and step statuses.
 * Returns practice agreement route if practice profile is complete, otherwise returns the next incomplete step route.
 *
 * @param {Omit<Practice, "taxInformation" | "taxInfo" | "practiceAffiliates" | "practiceInvites">} practice - The practice object containing status and other relevant information.
 * @param {PracticeCompleteness} practiceCompleteness - The practice completeness object containing booleans for stages of practice completeness.
 * @param {bool} userHasCompletedOfficeSetup - The flag that is set when the user has completed entering all office location information.
 * @param {bool} userCompletedTaxInformation - The flag that is set when the user has completed entering all tax information.
 * @param {bool} userHasCompletedLiabilityInformation - The flag that is set when the user has completed entering all liability information.
 * @param {PracticeProfileStep[]} practiceStepStatuses - An array of practice profile steps with their current statuses.
 * @returns {string | null} The route to the next incomplete step. If there is no next step or the practice status is not pending,
 *                          it returns the practice information address route as a default. Returns null only if the practice status is not pending.
 */
export const getNextIncompletePracticeStepRoute = (
  practice: Omit<
    Practice,
    "taxInformation" | "taxInfo" | "practiceAffiliates" | "practiceInvites"
  >,
  practiceCompleteness: PracticeCompleteness,
  userHasCompletedOfficeSetup: boolean,
  userCompletedTaxInformation: boolean,
  userHasCompletedLiabilityInformation: boolean,
) => {
  if (!practice.id) {
    return null
  }

  const primaryLocation = practice.locations.find(
    (location) => location.isPrimary,
  )

  const sequentialRequiredPracticeSteps = [
    {
      route: appRoutes.practice.practiceInformation.ADDRESS_ROUTE,
      complete: isPracticeAddressComplete(
        practice.practiceInfo.practiceAddress,
      ),
    },
    {
      route: appRoutes.practice.practiceInformation.CONTACT_ROUTE,
      complete: isPracticeContactComplete(
        practice.practiceInfo.practiceContact,
      ),
    },
    {
      route: appRoutes.practice.TAX_ROUTE,
      complete: true,
    },
    {
      route: appRoutes.practice.taxInformation.TIN_ROUTE,
      complete: userCompletedTaxInformation,
    },
    {
      route: appRoutes.practice.location.ADDRESS_ROUTE,
      complete: !!primaryLocation
        ? isLocationAddressComplete(primaryLocation)
        : true,
    },
    {
      route: appRoutes.practice.location.CONTACT_ROUTE,
      complete: !!primaryLocation
        ? isLocationContactComplete(primaryLocation)
        : true,
    },
    {
      route: appRoutes.practice.location.DETAILS_ROUTE,
      complete: !!primaryLocation
        ? isLocationDetailsComplete(primaryLocation)
        : true,
    },
    {
      route: appRoutes.practice.OFFICES_ROUTE,
      complete: userHasCompletedOfficeSetup,
    },
    {
      route: appRoutes.practice.LIABILITY_ROUTE,
      complete: practiceCompleteness.isPracticeUsingLiabilityInsurance
        ? practiceCompleteness.isPracticeLiabilityInformationComplete
        : userHasCompletedLiabilityInformation,
    },
    {
      route: appRoutes.practice.PRACTICE_AGREEMENT_ROUTE,
      complete: practiceCompleteness.isPracticeAgreementComplete,
    },
  ]

  const incompleteSteps = sequentialRequiredPracticeSteps.filter(
    (step) => !step.complete,
  )

  const queryParams: Record<string, string> = !!primaryLocation
    ? {
        practiceId: practice.id,
        locationId: primaryLocation.id || "",
      }
    : {
        practiceId: practice.id,
      }

  const nextIncompleteStepRoute =
    incompleteSteps.length > 0
      ? getRouteWithQueryParams(incompleteSteps[0].route, queryParams)
      : getRouteWithQueryParams(
          appRoutes.practice.PRACTICE_AGREEMENT_ROUTE,
          queryParams,
        )

  return nextIncompleteStepRoute
}

/**
 * Determines the next incomplete provider profile step route based on the provider status and step statuses.
 * Returns provider Disclosure route if provider profile is complete, otherwise returns the next incomplete step route.
 *
 * @param {Provider} provider - The Provider object containing status and other relevant information.
 * @param {ProviderProfileStep[]} providerStepStatuses - An array of provider profile steps with their current statuses.
 * @returns {string | null} The route to the next incomplete step. If there is no next step or the practice status is not pending,
 *                          it returns the provider personal information contact route as a default.
 *                          Returns null only if the provider status is not pending.
 */
export const getNextIncompleteProviderStepRoute = (
  provider: Provider,
  providerOnboardingData: ProviderOnboardingData,
  is_M1_3_1_Always_Onboard_Locations_enabled?: boolean,
  IsNoVirtualLocationEnabled?: boolean,
) => {
  if (!provider.id) {
    return null
  }

  const providerNeedsLocations =
    !!is_M1_3_1_Always_Onboard_Locations_enabled ||
    (providerOnboardingData.affiliatedPractice?.locations?.length ?? 0) > 1
  const providerNeedsInsurance =
    !providerOnboardingData.affiliatedPractice?.authReleaseRequired

  const sequentialRequiredProviderSteps = [
    {
      route: appRoutes.provider.personalInformation.CONTACT_ROUTE,
      complete: personalInfoContactComplete(provider),
    },
    {
      route: appRoutes.provider.personalInformation.DETAILS_ROUTE,
      complete: personInfoDetailsComplete(provider),
    },
    {
      route: appRoutes.provider.clientFocus.SPECIALTIES_ROUTE,
      complete: clientFocusSpecialtiesComplete(provider),
    },
    {
      route: appRoutes.provider.clientFocus.DEMOGRAPHICS_ROUTE,
      complete: clientFocusDemographicsComplete(provider),
    },
    {
      route: appRoutes.provider.clientFocus.SESSIONS_ROUTE,
      // Do not allow routing to sessions step if the No Virtual Location FF is enabled, as this step is not required in that case. The step's completion status will be determined as complete when the FF is enabled.
      complete: IsNoVirtualLocationEnabled
        ? true
        : clientFocusSessionsComplete(provider),
    },
    {
      route: appRoutes.provider.availability.DAYS_OF_OPERATION_ROUTE,
      complete: getAvailabilityStepStatus(provider) === StepStatusEnum.COMPLETE,
    },
    {
      route: appRoutes.provider.qualifications.LICENSES_ROUTE,
      complete:
        getQualificationsStepStatus(provider) === StepStatusEnum.COMPLETE,
    },
    {
      route: appRoutes.provider.INSURANCE_ROUTE,
      complete: getInsuranceStepStatus(provider) === StepStatusEnum.COMPLETE,
    },
    {
      route: appRoutes.provider.SERVICES_ROUTE,
      // If the No Virtual Location FF is not enabled, the services step is not required and its completion status is determined as complete, allowing users to proceed to the next steps without having to attest to virtual services.
      complete: IsNoVirtualLocationEnabled
        ? getServiceStepStatus(provider) === StepStatusEnum.COMPLETE
        : true,
    },
    providerNeedsLocations && {
      route: appRoutes.provider.LOCATIONS_ROUTE,
      // If the No Virtual Location FF is enabled, the locations step is not required and its completion status is determined as complete, allowing users to proceed to the next steps without having to enter location information.
      complete: IsNoVirtualLocationEnabled
        ? true
        : getLocationsStepStatus(provider) === StepStatusEnum.COMPLETE,
    },
    providerNeedsInsurance && {
      route: appRoutes.provider.LIABILITY_INSURANCE_ROUTE,
      complete:
        getLiabilityInsuranceStepStatus(provider) === StepStatusEnum.COMPLETE,
    },
    {
      route: appRoutes.provider.DISCLOSURE_ROUTE,
      complete: getDisclosureStepStatus(provider) === StepStatusEnum.COMPLETE,
    },
  ]

  const incompleteSteps = sequentialRequiredProviderSteps
    .filter((step) => !!step)
    .filter((step) => !step.complete)

  const nextIncompleteStepRoute =
    incompleteSteps.length > 0
      ? getRouteWithQueryParams(incompleteSteps[0].route, {
          providerId: provider.id,
        })
      : getRouteWithQueryParams(appRoutes.provider.DISCLOSURE_ROUTE, {
          providerId: provider.id,
        })

  return nextIncompleteStepRoute
}
